// Fetch/XHR URL rewrite helpers
// Provides enable/disable/setup utilities for selective path rewriting.

declare global {
  interface Window {
    ccDisableFetchRewrite?: () => void;
    __ccFetchOrig?: typeof fetch;
    __ccXHROrigOpen?: typeof XMLHttpRequest.prototype.open;
    __ccXHROrigSetRequestHeader?: typeof XMLHttpRequest.prototype.setRequestHeader;
    communityUrl?: string;
  }
}

function enableFetchRewrite(targetUrl: string, paths: string[] = ['/search/searchToken', '/phrase/batch', '/widget/featuredBanner']): () => void {
  const PATHS = new Set(paths);
  const shouldRewrite = (pathname: string) => PATHS.has(pathname);

  const rewriteUrl = (input: RequestInfo | URL): string => {
    try {
      const originalInput = typeof input === 'string' ? input : (input instanceof URL ? input.href : (input as Request).url);
      const url = new URL(originalInput, window.location.origin);
      if (!shouldRewrite(url.pathname)) return originalInput;
      return new URL(url.pathname + url.search + url.hash, targetUrl).href;
    } catch (e) {
      console.error('URL rewrite error:', e, 'for input:', input);
      return String(input as any);
    }
  };

  if (!window.__ccFetchOrig) window.__ccFetchOrig = window.fetch.bind(window);
  if (!window.__ccXHROrigOpen) window.__ccXHROrigOpen = XMLHttpRequest.prototype.open;
  if (!window.__ccXHROrigSetRequestHeader) window.__ccXHROrigSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;

  window.fetch = (async (input: RequestInfo | URL, init: RequestInit = {}) => {
    const req = input instanceof Request ? input : new Request(input, init);
    const rewrittenUrl = rewriteUrl(req.url);
    const isRewritten = rewrittenUrl !== req.url;

    let headers = new Headers(req.headers);
    if (isRewritten) {
      const filtered = new Headers();
      headers.forEach((value, name) => {
        if (!['x-csrf-token', 'x-csrftoken', 'x-requested-with'].includes(name.toLowerCase())) {
          filtered.set(name, value);
        }
      });
      headers = filtered;
    }

    const reqInit: RequestInit = {
      method: req.method,
      headers,
      body: (req as any).body ?? init.body,
      mode: init.mode ?? req.mode,
      cache: init.cache ?? req.cache,
      redirect: init.redirect ?? req.redirect,
      referrer: init.referrer ?? req.referrer,
      referrerPolicy: init.referrerPolicy ?? req.referrerPolicy,
      integrity: init.integrity ?? req.integrity,
      keepalive: init.keepalive ?? req.keepalive,
      signal: init.signal ?? req.signal,
      credentials: init.credentials ?? req.credentials ?? 'same-origin',
    };

    return window.__ccFetchOrig!(rewrittenUrl, reqInit);
  }) as typeof fetch;

  const _open = window.__ccXHROrigOpen;
  const _setRequestHeader = window.__ccXHROrigSetRequestHeader;
  (XMLHttpRequest.prototype as any)._isRewritten = false;
  type OpenRest = [async?: boolean, username?: string | null, password?: string | null];
  XMLHttpRequest.prototype.open = function (this: XMLHttpRequest, method: string, url: string, ...rest: OpenRest) {
    const rewritten = rewriteUrl(url);
    (this as any)._isRewritten = rewritten !== url;
    if (rest.length === 0) return _open.call(this, method, rewritten, true);
    if (rest.length === 1) return _open.call(this, method, rewritten, rest[0] ?? true);
    if (rest.length === 2) return _open.call(this, method, rewritten, rest[0] ?? true, rest[1]!);
    return _open.call(this, method, rewritten, rest[0] ?? true, rest[1]!, rest[2]!);
  } as any;

  XMLHttpRequest.prototype.setRequestHeader = function (this: XMLHttpRequest, name: string, value: string) {
    const blocked = ['x-csrf-token', 'x-csrftoken', 'x-requested-with'];
    if ((this as any)._isRewritten && blocked.includes(name.toLowerCase())) return;
    return _setRequestHeader.call(this, name, value);
  } as any;

  const disable = () => {
    if (window.__ccFetchOrig) {
      window.fetch = window.__ccFetchOrig;
      delete window.__ccFetchOrig;
    }
    if (window.__ccXHROrigOpen) {
      XMLHttpRequest.prototype.open = window.__ccXHROrigOpen;
      delete window.__ccXHROrigOpen;
    }
    if (window.__ccXHROrigSetRequestHeader) {
      XMLHttpRequest.prototype.setRequestHeader = window.__ccXHROrigSetRequestHeader;
      delete window.__ccXHROrigSetRequestHeader;
    }
    delete window.ccDisableFetchRewrite;
  };

  window.ccDisableFetchRewrite = disable;
  console.log(`Fetch rewrite enabled for ['/search/searchToken','/phrase/batch'] -> ${targetUrl}`);
  return disable;
}

export function setupFetchRewrite(options: { enabled?: boolean; targetUrl?: string | null } = {}) {
  const { enabled = true, targetUrl = null } = options;
  if (!enabled) return;
  let finalTargetUrl: string | null = targetUrl ?? null;
  if (!finalTargetUrl) {
    if (typeof window.communityUrl !== 'undefined') finalTargetUrl = window.communityUrl!;
    else {
      const el = document.querySelector('cc-generic-hero-search');
      if (el) {
        finalTargetUrl = el.getAttribute('community-url')
          || el.getAttribute('data-community-url')
          ;
      }
    }
  }
  if (!finalTargetUrl) {
    console.warn('Fetch rewrite: No target URL found. Define communityUrl or set data-community-url on <cc-generic-hero-search>.');
    return;
  }
  enableFetchRewrite(finalTargetUrl);
}
